import classNames from 'classnames';
import { find, isEmpty, isEqual, uniq } from 'lodash';
import * as React from 'react';

import { Bem } from '../../service/Bem/Bem';
import { TestBem } from '../../service/TestBem';
import {
    CheckBoxChangeData,
    Dictionary,
    DisplayListItem,
    DisplayRegion,
    FiltersValue,
    MapType,
    PolygonData,
    SelectedScreens
} from '../../typings/displayMap';
import { CheckboxDecoratorCheckedItem } from '../CheckboxDecorator/CheckboxDecorator';
import { DisplayChooser } from '../DisplayChooser/DisplayChooser';
import { selectedFilters as easySelectedFilters } from '../EasyFilters/EasyFilters';
import { Loader } from '../Loader/Loader';
import { RegionSelect } from '../RegionSelect/RegionSelect';

import './DisplayMapProvider.css';
import { IndoorUtils } from './Indoor/Indoor';
import { OutdoorUtils } from './Outdoor/Outdoor';

export interface DisplayMapProviderProps {
    screenGroupsByRegion: {
        [id: string]: DisplayListItem[];
    };
    regions: DisplayRegion[];
    mapType: MapType;
    dictionary: Dictionary;
    onChange: (checkedByRegion: SelectedScreens) => void;
    selectedScreens?: SelectedScreens;
    drawPolygonsDrawer?: boolean;
    isNewInterface?: boolean;
}

export interface DisplayMapProviderState {
    filteredRegions: DisplayRegion[];
    filteredScreenGroupsByRegion: {
        [id: string]: DisplayListItem[];
    };
    selectedScreens: SelectedScreens;
    noOneRegionSelected: boolean;
    isAnimating: boolean;
    showAddressPoint: boolean;
    scrollToRow?: string | undefined;
    filtersValue: FiltersValue;
    selectedFilters: {
        [name: string]: easySelectedFilters;
    };
    highlightedGroups: string[];
    polygonData: PolygonData;
    zoomToRegion?: string;
    zoomToGroup?: DisplayListItem;
    searchText: string;
    isMapLoaded: boolean;
}

export class DisplayMapProvider extends React.Component<DisplayMapProviderProps, DisplayMapProviderState> {
    private block = 'display-map-provider';

    constructor(props: DisplayMapProviderProps) {
        super(props);

        this.state = {
            filteredRegions: this.props.regions,
            filteredScreenGroupsByRegion: this.props.screenGroupsByRegion,
            selectedScreens: this.props.selectedScreens || {},
            isAnimating: false,
            filtersValue: this.getFiltersValue(),
            selectedFilters: {},
            highlightedGroups: [],
            zoomToRegion: '',
            showAddressPoint: false,
            noOneRegionSelected: true,
            searchText: '',
            polygonData: {
                activePolygon: undefined,
                isDrawEnabled: false
            },
            isMapLoaded: false
        };
    }

    render() {
        const {
            filteredRegions,
            filteredScreenGroupsByRegion,
            selectedScreens,
            scrollToRow,
            filtersValue,
            selectedFilters,
            highlightedGroups,
            zoomToRegion,
            zoomToGroup,
            showAddressPoint,
            polygonData,
            searchText
        } = this.state;

        const { mapType, dictionary, drawPolygonsDrawer, isNewInterface } = this.props;

        return (
            <>
                {!this.state.isMapLoaded && <Loader size={'l'} />}
                {this.buildRegionSelector()}
                <DisplayChooser
                    mapType={mapType}
                    zoomToRegion={zoomToRegion}
                    zoomToGroup={zoomToGroup}
                    filteredRegions={filteredRegions}
                    filteredScreenGroupsByRegion={filteredScreenGroupsByRegion}
                    selectedScreens={selectedScreens}
                    scrollToRow={scrollToRow}
                    filtersValue={filtersValue}
                    selectedFilters={selectedFilters}
                    highlightedGroups={highlightedGroups}
                    dictionary={dictionary}
                    showAddressPoint={showAddressPoint}
                    drawPolygonsDrawer={drawPolygonsDrawer}
                    searchText={searchText}
                    onAnimationComplete={this.animationComplete}
                    onSuggestItemSelect={this.onSuggestItemSelect}
                    onScreensSelect={this.selectedGroup}
                    onFiltersChange={this.onFiltersChange}
                    onAllGroupsSelect={this.onAllGroupsSelect}
                    screensCount={this.getScreensCount()}
                    onPlatformClick={this.onPlatformClick}
                    onBalloonCaptionClick={this.onBalloonCaptionClick}
                    onAddressGroupClick={this.onAddressGroupClick}
                    onPolygonsListChange={this.onPolygonsListChange}
                    onActivePolygonChange={this.onActivePolygonChange}
                    polygonData={polygonData}
                    onSearchInput={this.onSearchInput}
                    onMapLoaded={this.onMapLoaded}
                    isNewInterface={isNewInterface}
                />
            </>
        );
    }

    private onMapLoaded = (isMapLoaded: boolean) => {
        this.setState({ isMapLoaded });
    };

    private onSearchInput = (searchText: string) => {
        this.setState({
            filteredScreenGroupsByRegion: this.filterScreenGroupsByRegion({
                searchText
            }),
            searchText
        });
    };

    private getScreensCount = () => {
        const { screenGroupsByRegion } = this.props;

        return Object.keys(screenGroupsByRegion).reduce((length: number, regionId: string) => {
            screenGroupsByRegion[regionId].map((group: DisplayListItem) => {
                length += group.screens.length;
            });

            return length;
        }, 0);
    };

    private onSuggestItemSelect = (elementProps: { id: string }) => {
        this.setState(
            {
                isAnimating: true,
                scrollToRow: elementProps.id,
                highlightedGroups: [elementProps.id],
                showAddressPoint: true
            },
            () => {
                this.setState({ showAddressPoint: false });
            }
        );
    };

    private onFiltersChange = (newSelectedFilters: { [name: string]: easySelectedFilters }) => {
        this.setState({
            selectedFilters: newSelectedFilters,
            filteredScreenGroupsByRegion: this.filterScreenGroupsByRegion({
                filters: newSelectedFilters
            }),
            highlightedGroups: []
        });
    };

    private filterScreenGroupsByRegion = (params: {
        filters?: { [name: string]: easySelectedFilters };
        regions?: DisplayRegion[];
        selected?: SelectedScreens;
        polygonData?: PolygonData;
        searchText?: string;
    }) => {
        const { mapType, screenGroupsByRegion } = this.props;
        const filters = params.filters || this.state.selectedFilters;
        const regions = params.regions || this.state.filteredRegions;
        const selected = params.selected || this.state.selectedScreens;
        const polygonData = params.polygonData || this.state.polygonData;
        const searchText = params.searchText !== undefined ? params.searchText : this.state.searchText;

        switch (mapType) {
            case MapType.indoor:
                return IndoorUtils.filterScreenGroups(
                    screenGroupsByRegion,
                    filters,
                    regions,
                    selected,
                    polygonData,
                    searchText
                );
            case MapType.outdoor:
                return OutdoorUtils.filterScreenGroups(
                    screenGroupsByRegion,
                    filters,
                    regions,
                    selected,
                    polygonData,
                    searchText
                );
            default:
                return {};
        }
    };

    private getFiltersValue = () => {
        const { mapType, screenGroupsByRegion } = this.props;

        switch (mapType) {
            case MapType.indoor:
                return IndoorUtils.getFilters(screenGroupsByRegion);
            case MapType.outdoor:
                return OutdoorUtils.getFilters(screenGroupsByRegion);
            default:
                return {};
        }
    };

    private buildRegionSelector = () => {
        const { regions, isNewInterface } = this.props;
        const { filteredRegions, noOneRegionSelected, isMapLoaded } = this.state;

        return (
            <RegionSelect
                cls={classNames(
                    Bem.cls(this.block, 'region-select'),
                    TestBem.cls({
                        block: this.block,
                        elem: 'region-select',
                        mods: {
                            loaded: isMapLoaded ? 'yes' : 'no'
                        }
                    })
                )}
                regions={regions.map(region => {
                    return {
                        id: region.id,
                        text: region.text,
                        screensCount: this.getSelectedScreensCount(region.id),
                        goToRegionLocation: this.onGoToRegionLocation,
                        uncheckAllPlatforms: this.onUncheckAllPlatforms
                    };
                })}
                noOneRegionSelected={noOneRegionSelected}
                selected={filteredRegions.map(region => region.id)}
                onChange={this.onRegionSelectChange}
                isNewInterface={isNewInterface}
            />
        );
    };

    private onUncheckAllPlatforms = (regionId: string) => {
        const { selectedScreens } = this.state;
        const newSelectedScreens = {
            ...selectedScreens,
            [regionId]: []
        };

        this.setState({
            selectedScreens: newSelectedScreens,
            highlightedGroups: []
        });

        this.props.onChange(newSelectedScreens);
    };

    private onGoToRegionLocation = (regionId: string) => {
        this.setState({ zoomToRegion: regionId }, () => {
            this.setState({ zoomToRegion: undefined });
        });
    };

    private onAddressGroupClick = (group: DisplayListItem) => {
        this.setState({ zoomToGroup: group }, () => {
            this.setState({
                zoomToGroup: undefined,
                highlightedGroups: []
            });
        });
    };

    private getSelectedScreensCount = (regionId: string) => {
        const { selectedScreens } = this.state;
        const { screenGroupsByRegion } = this.props;

        if (selectedScreens[regionId]) {
            return selectedScreens[regionId].reduce((length: number, groupId: string) => {
                const group = find(screenGroupsByRegion[regionId], {
                    id: groupId
                });

                if (group) {
                    length += group.screens.length;
                }

                return length;
            }, 0);
        } else {
            return 0;
        }
    };

    private onRegionSelectChange = (data: { add: [string]; remove: [string] }) => {
        const { filteredRegions } = this.state;
        const { regions } = this.props;
        let newFilteredRegions = [...filteredRegions];

        if (!isEmpty(data.remove)) {
            newFilteredRegions = newFilteredRegions.filter(region => !data.remove.includes(region.id));
        }

        if (!isEmpty(data.add)) {
            data.add.forEach(id => {
                const region = find(regions, { id: id });

                if (region) {
                    newFilteredRegions.push(region);
                }
            });
        }

        newFilteredRegions = regions.filter(region => !!find(newFilteredRegions, { id: region.id }));

        const noOneRegionSelected = newFilteredRegions.length === 0;

        // TODO - не анчекать последний регион
        this.setState({
            filteredRegions: noOneRegionSelected ? regions : newFilteredRegions,
            filteredScreenGroupsByRegion: this.filterScreenGroupsByRegion({
                regions: noOneRegionSelected ? regions : newFilteredRegions
            }),
            noOneRegionSelected,
            highlightedGroups: []
        });
    };

    private onPlatformClick = (groupId: string[]) => {
        this.setState({
            scrollToRow: groupId[0],
            highlightedGroups: groupId
        });
    };

    private onPolygonsListChange = (polygons: ymaps.GeoObjectCollection, isDrawEnabled: boolean) => {
        const polygonData = {
            ...this.state.polygonData,
            polygons,
            isDrawEnabled
        };

        this.setState({
            polygonData,
            filteredScreenGroupsByRegion: this.filterScreenGroupsByRegion({
                polygonData
            })
        });
    };

    private onActivePolygonChange = (polygon: ymaps.Polygon) => {
        this.setState({
            polygonData: {
                ...this.state.polygonData,
                activePolygon: polygon
            }
        });
    };

    private onBalloonCaptionClick = (groupId: string) => {
        this.setState({
            scrollToRow: groupId,
            highlightedGroups: [groupId]
        });
    };

    private animationComplete = () => {
        this.setState({
            isAnimating: false,
            scrollToRow: undefined
        });
    };

    private selectedGroup = (data: CheckBoxChangeData) => {
        const { selectedScreens } = this.state;
        const newSelectedScreens = {
            ...selectedScreens,
            [data.regionId]: data.checked
                ? [...(selectedScreens[data.regionId] || []), data.id]
                : selectedScreens[data.regionId].filter(itemId => itemId !== data.id)
        };

        this.setState({
            selectedScreens: newSelectedScreens,
            highlightedGroups: []
        });

        this.props.onChange(newSelectedScreens);
    };

    private onAllGroupsSelect = (e: React.KeyboardEvent<HTMLElement>, data: CheckboxDecoratorCheckedItem) => {
        const { filteredScreenGroupsByRegion, selectedScreens } = this.state;
        let checkedScreenGroups;

        if (data.checked) {
            checkedScreenGroups = Object.keys(filteredScreenGroupsByRegion).reduce(
                (checkedScreens: SelectedScreens, regionId: string) => {
                    const regionSelectedScreens = selectedScreens[regionId] || [];

                    checkedScreens[regionId] = uniq(
                        regionSelectedScreens.concat(this.getFilteredItemsIdByRegion(regionId))
                    );

                    return checkedScreens;
                },
                {}
            );
        } else {
            checkedScreenGroups = Object.keys(filteredScreenGroupsByRegion).reduce(
                (checkedScreens: SelectedScreens, regionId: string) => {
                    if (selectedScreens[regionId]) {
                        const itemsId = this.getFilteredItemsIdByRegion(regionId);

                        checkedScreens[regionId] = selectedScreens[regionId].filter((displayItemId: string) => {
                            return !itemsId.includes(displayItemId);
                        });
                    }

                    return checkedScreens;
                },
                {}
            );
        }

        const newSelectedScreens = {
            ...selectedScreens,
            ...checkedScreenGroups
        };

        this.setState({
            selectedScreens: newSelectedScreens,
            highlightedGroups: []
        });

        if (!isEqual(newSelectedScreens, selectedScreens)) {
            this.props.onChange(newSelectedScreens);
        }
    };

    private getFilteredItemsIdByRegion = (regionId: string) => {
        const { filteredScreenGroupsByRegion } = this.state;

        return filteredScreenGroupsByRegion[regionId].map((displayItem: DisplayListItem) => {
            return displayItem.id;
        });
    };
}
